import { Autolinker, UrlMatch } from 'autolinker';
import { Group, MentionTarget, UserInfo } from '@/types';

// URLの正規表現 複雑なので変数を分けている
// linkify-itやautolinkerだとURL途中の括弧や文中にURLと日本語が混在している時などでうまく抽出できなかったので
// パス部分とクエリストリングのvalueは英数字記号/エンコードされたマルチバイトを許容している
// クエリストリング英数字と`-`,`_`, `[]`, エンコードされた文字列は許容している
// see: https://triple-underscore.github.io/URL-ja.html#urls
const URL_SCHEME_REGEXP = 'https?:\\/\\/';
const URL_PATH_REGEXP = "[-\\w.~!#$&'{}()*+,/:;=@[\\]]";
const URL_MULTI_BYTE_REG_EXP = '%[0-9a-fA-F]{2}';
const URL_QUERY_STRING_REGEXP = `(?:\\??((${URL_PATH_REGEXP}|${URL_MULTI_BYTE_REG_EXP})+=([-\\w[\\]]|${URL_MULTI_BYTE_REG_EXP})+&?)+)*`;
const HTTP_URL_REGEXP_STR_WITHOUT_BRACKET = `(\\b(?<url>${URL_SCHEME_REGEXP}(?:${URL_PATH_REGEXP}|${URL_MULTI_BYTE_REG_EXP})+${URL_QUERY_STRING_REGEXP}))`;
// 文章(URL)文章 から `(URL)` を抽出する正規表現. 文章([URL])文章の場合はこの正規表現だけだと[URL](内側の[]がとれない)がとれるので
// ここだけautolinkerでURLを抽出している
const HTTP_URL_REGEXP_STR_WITH_BRACKET = `(?<bracketUrl>([[{(]+\\s*${URL_SCHEME_REGEXP}(?:${URL_PATH_REGEXP}|${URL_MULTI_BYTE_REG_EXP})+${URL_QUERY_STRING_REGEXP}\\s*[\\])}]+))`;
const HTTP_URL_REGEXP = `${HTTP_URL_REGEXP_STR_WITHOUT_BRACKET}|${HTTP_URL_REGEXP_STR_WITH_BRACKET}`;

export const getSortedMentionTargets = (
  targets: (UserInfo & Group)[],
  user: UserInfo,
): MentionTarget[] => {
  return targets
    .slice()
    .filter(t => t.group_type !== 'all_user_group')
    .sort((t1, t2) => {
      const name1 = t1.user_name ? t1.user_name : t1.name;
      const name2 = t2.user_name ? t2.user_name : t2.name;
      return name2.length - name1.length;
    })
    .map(t => {
      return {
        target: t,
        type: t.user_name ? 'user' : 'group',
        name: t.user_name ?? t.name,
        link: t.user_name ? `/#/users/${t.id}` : `/#/groups/${t.id}`,
        mentionToUser: t.user_name
          ? t.id === user.id
          : targets
              .filter(g => g.is_member && g.group_type !== 'all_user_group')
              .includes(t),
      };
    });
};

const parseIndividualName = (
  user: UserInfo,
  targets: (UserInfo & Group)[],
  text: string,
): string => {
  let parsedText = text;
  const targetListSortedByNameLength = getSortedMentionTargets(targets, user);
  for (const target of targetListSortedByNameLength) {
    const mentionName = RegExp(`@${escapeRegex(target.name)}`, 'g');
    if (target.mentionToUser && mentionName.test(parsedText)) {
      parsedText = parsedText.replace(
        mentionName,
        `<a class="mention-name mention-to-user">${target.type}${target.target.id}</a>`,
      );
    } else if (mentionName.test(parsedText)) {
      parsedText = parsedText.replace(
        mentionName,
        `<a class="mention-name">${target.type}${target.target.id}</a>`,
      );
    }
  }
  for (const target of targetListSortedByNameLength) {
    let mentionName = RegExp(
      `<a class="mention-name mention-to-user">${target.type}${target.target.id}</a>`,
      'g',
    );
    parsedText = parsedText.replace(
      mentionName,
      `<a href="${target.link}" class="mention-name mention-to-user">@${target.name}</a>`,
    );
    mentionName = RegExp(
      `<a class="mention-name">${target.type}${target.target.id}</a>`,
      'g',
    );
    parsedText = parsedText.replace(
      mentionName,
      `<a href="${target.link}" class="mention-name">@${target.name}</a>`,
    );
  }
  return parsedText;
};

const parseMentionName = (
  text: string,
  users: UserInfo[],
  groups: Group[],
  user: UserInfo,
) => {
  let parsedText = text;
  const mentionAll = RegExp('@全員', 'g');
  if (mentionAll.test(parsedText)) {
    parsedText = parsedText.replace(mentionAll, match => {
      return `<a class="mention-name mention-to-user">${match}</a>`;
    });
  }
  const targets = [...users, ...groups] as (UserInfo & Group)[];
  parsedText = parseIndividualName(user, targets, parsedText);
  return parsedText;
};

// テーマ名やユーザー名に半角の()がはいってると正規表現が壊れるのでエスケープ

export const escapeRegex = (string: string) => {
  return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};

export const urlParse = (text: string): string => {
  return makeAnchor(text);
};

export const commentParse = (
  text: string,
  users: any[], // eslint-disable-line @typescript-eslint/no-explicit-any
  groups: Group[],
  user: UserInfo,
  articleUrl?: string,
): string => {
  if (!users) return text;
  if (text === articleUrl) return urlToLink(text);

  let anchorText = makeAnchor(text, articleUrl);
  const hasAtSign = /.*@.*/;
  if (hasAtSign.test(anchorText)) {
    anchorText = parseMentionName(anchorText, users, groups, user);
  }
  return anchorText;
};

export const isHttpUrl = (url?: string): url is string => {
  if (url === undefined) return false;
  return url?.startsWith('https://') || url?.startsWith('http://');
};

export const isValidHttpUrl = (url?: string) => {
  if (url === undefined) return false;

  try {
    // URLオブジェクトで不正なURLを弾く
    new URL(url);
    // 不完全なURIエンコードを弾く
    decodeURIComponent(url);
  } catch {
    return false;
  }

  return isHttpUrl(url) && httpUrlRegExpFactory().test(url);
};

export const extractUrls = (text: string): string[] => {
  const matched = text.matchAll(httpUrlRegExpFactory());
  if (!matched) return [];

  const urls: string[] = [];
  for (const m of matched) {
    const groups = m.groups;
    if (groups?.url) {
      urls.push(groups.url);
    } else if (groups?.bracketUrl) {
      const url = groups.bracketUrl;
      urls.push(...parseUrl(url));
    }
  }
  return urls;
};

const httpUrlRegExpFactory = (): RegExp => {
  return new RegExp(HTTP_URL_REGEXP, 'giu');
};

const makeAnchor = (text: string, removeArticleUrl?: string): string => {
  const regExp = httpUrlRegExpFactory();
  if (!regExp.test(text)) return text;

  return text.replace(regExp, (_, ...args) => {
    const groups = args[args.length - 1]; // 正規表現のgroup
    if (groups.url) {
      if (removeArticleUrl === groups.url) return '';
      return urlToLink(groups.url);
    } else {
      // (url) という形でとれるので, (<a href=""></a>)という形で返す
      const bracketUrl = groups.bracketUrl;
      const openBracket = bracketUrl.at(0);
      const closeBracket = bracketUrl.at(-1);
      const url = parseUrl(bracketUrl)[0];
      // 不完全なURLはautolinkerが認識せずurlがundefinedになるので、元のテキストのまま返す
      if (!url) return bracketUrl;
      const anchorLink = removeArticleUrl !== url ? urlToLink(url) : '';
      return `${openBracket}${anchorLink}${closeBracket}`;
    }
  });
};

const urlToLink = (url: string): string => {
  if (!url) return url;

  const link = parseUrl(url)[0];
  // 不完全なURLはautolinkerが認識せずlinkがundefinedになるので、元のテキストのまま返す
  if (!link) return url;
  return `<a href="${link}" class="url-link" target="_blank" rel="noopener noreferrer">${decodeURIComponent(link)}</a>`;
};

const parseUrl = (url: string): string[] => {
  const matches =
    Autolinker.parse(url, {
      urls: true,
      email: false,
      phone: false,
      mention: false,
    }) ?? [];
  return matches
    .filter<UrlMatch>(v => v instanceof UrlMatch)
    .map(v => v.getAnchorHref()); // URL内に `&amp;` が入ってきたら & に置換している
};
